import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/posthog";
import { designsRemaining } from "@/lib/referrals";
import { PLAN_LIMITS, planFromRow, type Plan } from "@/lib/plans";

export const FREE_MONTHLY_LIMIT = PLAN_LIMITS.free.designs;
export const PRO_MONTHLY_LIMIT = PLAN_LIMITS.pro.designs;
export const MONTHLY_GENERATION_LIMIT = FREE_MONTHLY_LIMIT;


export interface GenerationQuota {
  used: number;
  limit: number;
  remaining: number;
  bonus: number;
  resetAt: Date | null;
  unlimited: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
}

function computeResetAt(periodStart: string | null): Date | null {
  if (!periodStart) return null;
  const start = new Date(periodStart);
  if (Number.isNaN(start.getTime())) return null;
  // Period rolls at the start of next calendar month (UTC).
  return new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
}

function isUnlimitedEmail(email: string | null | undefined): boolean {
  return !!email && email.toLowerCase().endsWith("@huelo.co");
}

// Session flag so we only ask the backend to sweep orphaned refunds
// once per browser tab, not on every component remount / route change.
const SWEEP_SESSION_KEY = "huelo:refund-sweep-done";

async function sweepOrphanedRefundsOncePerSession(): Promise<boolean> {
  if (typeof sessionStorage === "undefined") return false;
  if (sessionStorage.getItem(SWEEP_SESSION_KEY)) return false;
  // Mark before awaiting so a second concurrent caller skips.
  sessionStorage.setItem(SWEEP_SESSION_KEY, "1");
  try {
    const { data, error } = await supabase.functions.invoke<{
      refunded?: number;
    }>("sweep-my-refunds", { body: {} });
    if (error) {
      // Allow retry on next session if the call itself failed.
      sessionStorage.removeItem(SWEEP_SESSION_KEY);
      return false;
    }
    return (data?.refunded ?? 0) > 0;
  } catch {
    sessionStorage.removeItem(SWEEP_SESSION_KEY);
    return false;
  }
}

export function useGenerationQuota(): GenerationQuota {
  const [used, setUsed] = useState(0);
  const [resetAt, setResetAt] = useState<Date | null>(null);
  const [unlimited, setUnlimited] = useState(false);
  const [plan, setPlan] = useState<Plan>("free");
  const [bonus, setBonus] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const user = userData.user;
      if (!user) {
        setUsed(0);
        setResetAt(null);
        setUnlimited(false);
        setPlan("free");
        return;
      }

      if (isUnlimitedEmail(user.email)) {
        setUnlimited(true);
        setUsed(0);
        setResetAt(null);
        return;
      }
      setUnlimited(false);

      // Fetch plan from subscribers table (cached, updated by check-subscription).
      const { data: subRow } = await supabase
        .from("subscribers")
        .select("plan, status, current_period_end")
        .eq("user_id", user.id)
        .maybeSingle();
      const userPlan = planFromRow(subRow);
      setPlan(userPlan);
      const activeLimit = PLAN_LIMITS[userPlan].designs;

      // Reconcile orphaned generations from prior silently-killed runs
      // BEFORE reading the count, so the UI shows the post-refund value
      // on first paint. No-op after the first call of this session.
      await sweepOrphanedRefundsOncePerSession();

      const { data } = await supabase
        .from("profiles")
        .select("monthly_generation_count, monthly_period_start, bonus_designs")
        .eq("user_id", user.id)
        .maybeSingle();

      setBonus(data?.bonus_designs ?? 0);
      const now = new Date();
      const currentPeriodStart = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1),
      );
      const storedStart = data?.monthly_period_start
        ? new Date(data.monthly_period_start)
        : null;

      // If the stored period is older than the current month, the user's
      // count will reset on their next generation — show 0 used now.
      if (!storedStart || storedStart < currentPeriodStart) {
        setUsed(0);
        setResetAt(
          new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)),
        );
      } else {
        const usedNow = data?.monthly_generation_count ?? 0;
        const resetAtNow = computeResetAt(data?.monthly_period_start ?? null);
        setUsed(usedNow);
        setResetAt(resetAtNow);
        if (
          usedNow >= activeLimit && (data?.bonus_designs ?? 0) <= 0 &&
          typeof sessionStorage !== "undefined" &&
          !sessionStorage.getItem("huelo:quota-at-cap-tracked")
        ) {
          sessionStorage.setItem("huelo:quota-at-cap-tracked", "1");
          track("monthly_quota_state_at_cap", {
            used: usedNow,
            limit: activeLimit,
            reset_at: resetAtNow?.toISOString() ?? null,
          });
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    void load();
  }, [load]);

  const effectiveLimit = PLAN_LIMITS[plan].designs;
  return {
    used,
    limit: effectiveLimit,
    remaining: unlimited ? Number.POSITIVE_INFINITY : designsRemaining(effectiveLimit, used, bonus),
    bonus,
    resetAt,
    unlimited,
    loading,
    refresh: load,
  };
}


export function formatResetDate(date: Date | null): string {
  if (!date) return "next month";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
